import type {
  AdminConfigProfileActivationBlocker,
  GameConfigCatalogIdentity,
  GameConfigIdentity,
  TableConfigurationPendingRequest,
} from "@utopia-well-done-ltd/contracts";
import { type ReactNode, useState } from "react";
import { asFormError } from "../format.js";
import { ConfirmAction } from "./ConfirmAction.js";
import { SbaiFreezeNotice, useSbaiFreeze } from "./SbaiFreeze.js";

export interface ApprovedConfigProfileBase {
  config: GameConfigIdentity;
  displayName: string;
  launchDefault: boolean;
  limitProfileKey: string;
  paceKey: string;
  profileKey: string;
}

interface PendingRequestWithUnavailableAuditMetadata {
  auditMetadataStatus: "unavailable";
  target: GameConfigIdentity;
}

type ApprovedConfigProfilePendingRequest =
  | TableConfigurationPendingRequest
  | PendingRequestWithUnavailableAuditMetadata;

export interface ApprovedConfigProfileState<Profile extends ApprovedConfigProfileBase> {
  active: GameConfigIdentity;
  approvedProfiles: Profile[];
  blockers: AdminConfigProfileActivationBlocker[];
  catalog: GameConfigCatalogIdentity;
  pending: ApprovedConfigProfilePendingRequest | null;
  rolloutStatus: "active" | "certification_candidate" | "pending_activation" | "quarantined";
}

interface ApprovedConfigProfilesProps<Profile extends ApprovedConfigProfileBase> {
  canWrite: boolean;
  operatorProfilesEnabled?: boolean;
  configuration: ApprovedConfigProfileState<Profile>;
  gameName: string;
  onActivate: (profile: Profile, reason: string) => Promise<void>;
  onCancelPending: (reason: string) => Promise<void>;
  renderSettings: (profile: Profile) => ReactNode;
}

type Intent<Profile> = { kind: "activate"; profile: Profile } | { kind: "cancel" };

const blockerLabels: Record<AdminConfigProfileActivationBlocker, string> = {
  betting_window_open: "the current betting window is open",
  draft_wagers_present: "players have draft wagers",
  hand_in_progress: "a hand is in progress",
  result_display_in_progress: "the current result is still being displayed",
  round_in_progress: "a round is in progress",
  table_not_drained: "the table is not drained",
};

function sameIdentity(left: GameConfigIdentity, right: GameConfigIdentity) {
  return left.configId === right.configId && left.configSha256 === right.configSha256;
}

function hasUnavailableAuditMetadata(
  request: ApprovedConfigProfilePendingRequest,
): request is PendingRequestWithUnavailableAuditMetadata {
  return "auditMetadataStatus" in request && request.auditMetadataStatus === "unavailable";
}

function CopyableIdentity({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <span className="copyable-identity">
      <span className="copyable-identity-value mono-12">{value}</span>
      <button
        aria-label={`Copy ${label}`}
        className="btn btn-quiet btn-xs"
        onClick={() => void copy()}
        title={`Copy ${label}`}
        type="button"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}

/**
 * One certified-profile control used by every game operations screen.
 *
 * It deliberately has no numeric or free-form configuration fields. Operators can select only an
 * identity returned by the approved catalog, and a queued selection has to be cancelled explicitly
 * before this component will offer another activation.
 */
export function ApprovedConfigProfiles<Profile extends ApprovedConfigProfileBase>({
  canWrite,
  operatorProfilesEnabled = false,
  configuration,
  gameName,
  onActivate,
  onCancelPending,
  renderSettings,
}: ApprovedConfigProfilesProps<Profile>) {
  const freeze = useSbaiFreeze();
  const profileWritesEnabled =
    operatorProfilesEnabled || import.meta.env.VITE_CERTIFIED_CONFIG_PROFILES_ENABLED === "true";
  const mayActivate = canWrite && freeze.canWrite && profileWritesEnabled;
  // Cancellation removes queued risk and remains available if certification or the activation
  // kill switch is withdrawn after a request was scheduled.
  const mayCancel = canWrite && freeze.canWrite;
  const [intent, setIntent] = useState<Intent<Profile> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function confirm(reason: string) {
    if (!intent || submitting || !freeze.canWrite) return;
    setSubmitting(true);
    setActionError(null);
    try {
      if (intent.kind === "activate") await onActivate(intent.profile, reason);
      else await onCancelPending(reason);
      setIntent(null);
    } catch (caught) {
      setActionError(asFormError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  const pendingRequest = configuration.pending;
  const pendingProfile = pendingRequest
    ? (configuration.approvedProfiles.find((profile) =>
        sameIdentity(profile.config, pendingRequest.target),
      ) ?? null)
    : null;
  const activationLocked = pendingRequest !== null;
  const certificationCandidate = configuration.rolloutStatus === "certification_candidate";
  const quarantined = configuration.rolloutStatus === "quarantined";

  return (
    <section className="card approved-profiles">
      <SbaiFreezeNotice freeze={freeze} />
      <div className="card-bar approved-profiles-head">
        <div>
          <strong>Available {gameName} configurations</strong>
          <span className="cell-sub">
            Select an eligible saved revision. Changes wait until the table is empty and settlement
            is complete.
          </span>
        </div>
        <span
          className={`badge badge-${activationLocked || certificationCandidate || quarantined ? "pending" : "active"}`}
        >
          <span className="dot" />
          {configuration.rolloutStatus === "pending_activation"
            ? "Pending activation"
            : certificationCandidate
              ? "Certification candidate"
              : quarantined
                ? "Quarantined"
                : "Active"}
        </span>
      </div>

      {!profileWritesEnabled ? (
        <div className="banner banner-quiet approved-profile-certification" role="status">
          Profile changes are read-only while awaiting certification and an effective production
          allowlist.
        </div>
      ) : null}

      <dl className="approved-profile-current">
        <div>
          <dt>Active configuration ID</dt>
          <dd>
            <CopyableIdentity
              label="active configuration ID"
              value={configuration.active.configId}
            />
          </dd>
        </div>
        <div>
          <dt>Active SHA-256</dt>
          <dd>
            <CopyableIdentity label="active SHA-256" value={configuration.active.configSha256} />
          </dd>
        </div>
        <div>
          <dt>Catalog ID</dt>
          <dd>
            <CopyableIdentity label="catalog ID" value={configuration.catalog.catalogId} />
          </dd>
        </div>
        <div>
          <dt>Catalog SHA-256</dt>
          <dd>
            <CopyableIdentity label="catalog SHA-256" value={configuration.catalog.catalogSha256} />
          </dd>
        </div>
      </dl>

      {quarantined ? (
        <div className="banner approved-profile-quarantine" role="alert">
          An exact active or pending configuration identity is not selectable from this effective
          catalog. The table is quarantined and profile activation is unavailable.
        </div>
      ) : null}

      {certificationCandidate ? (
        <div className="banner banner-quiet approved-profile-certification" role="status">
          This catalog is staged as certification evidence. No profile becomes selectable until its
          exact ID and hash appear in the signed effective allowlist.
        </div>
      ) : null}

      {!pendingRequest && configuration.blockers.length > 0 ? (
        <div className="banner banner-quiet" role="status">
          Current activation blockers:{" "}
          {configuration.blockers.map((blocker) => blockerLabels[blocker]).join("; ")}.
        </div>
      ) : null}

      {pendingRequest ? (
        <div className="approved-profile-pending" role="status">
          <div>
            <strong>
              {pendingProfile?.displayName ?? pendingRequest.target.configId} is queued.
            </strong>
            <span className="cell-sub">
              {configuration.blockers.length > 0
                ? `Activation is waiting because ${configuration.blockers.map((blocker) => blockerLabels[blocker]).join("; ")}. `
                : "Activation is queued for the next safe boundary. "}
              Cancel it explicitly before choosing another profile.
            </span>
            <span className="cell-sub">
              Pending target ID:{" "}
              <CopyableIdentity
                label="pending configuration ID"
                value={pendingRequest.target.configId}
              />
            </span>
            <span className="cell-sub">
              Pending target SHA-256:{" "}
              <CopyableIdentity
                label="pending configuration SHA-256"
                value={pendingRequest.target.configSha256}
              />
            </span>
            {hasUnavailableAuditMetadata(pendingRequest) ? (
              <span className="cell-sub">
                Audit metadata is unavailable for this legacy pending request. Its exact target
                identity is retained for diagnosis and explicit cancellation.
              </span>
            ) : (
              <>
                <span className="cell-sub">
                  Requested by {pendingRequest.requestedBy.email} at {pendingRequest.requestedAt}
                  {" \u00b7 "}Audit {pendingRequest.auditEntryId}
                </span>
                <span className="cell-sub">Reason: {pendingRequest.reason}</span>
              </>
            )}
          </div>
          {mayCancel ? (
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => {
                setActionError(null);
                setIntent({ kind: "cancel" });
              }}
              type="button"
            >
              Cancel pending change
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="approved-profile-grid">
        {configuration.approvedProfiles.map((profile) => {
          const active = sameIdentity(profile.config, configuration.active);
          const queued = pendingRequest
            ? sameIdentity(profile.config, pendingRequest.target)
            : false;
          return (
            <article className="approved-profile" key={profile.profileKey}>
              <div className="approved-profile-title">
                <div>
                  <strong>{profile.displayName}</strong>
                  <span className="cell-sub mono-12">{profile.profileKey}</span>
                </div>
                <div className="approved-profile-badges">
                  {active ? <span className="chip">Active</span> : null}
                  {queued ? <span className="chip">Pending</span> : null}
                  {profile.launchDefault ? <span className="chip">Launch default</span> : null}
                </div>
              </div>

              <dl className="approved-profile-settings">
                <div>
                  <dt>Pace key</dt>
                  <dd className="mono-12">{profile.paceKey}</dd>
                </div>
                <div>
                  <dt>Limit profile</dt>
                  <dd className="mono-12">{profile.limitProfileKey}</dd>
                </div>
                {renderSettings(profile)}
              </dl>

              <dl className="approved-profile-identity">
                <div>
                  <dt>Configuration ID</dt>
                  <dd>
                    <CopyableIdentity
                      label={`${profile.displayName} configuration ID`}
                      value={profile.config.configId}
                    />
                  </dd>
                </div>
                <div>
                  <dt>SHA-256</dt>
                  <dd>
                    <CopyableIdentity
                      label={`${profile.displayName} SHA-256`}
                      value={profile.config.configSha256}
                    />
                  </dd>
                </div>
              </dl>

              {mayActivate && !active && !certificationCandidate && !quarantined ? (
                <button
                  className="btn btn-sm"
                  disabled={activationLocked}
                  onClick={() => {
                    setActionError(null);
                    setIntent({ kind: "activate", profile });
                  }}
                  type="button"
                >
                  Activate {profile.displayName}
                </button>
              ) : null}
            </article>
          );
        })}
      </div>

      {intent ? (
        <ConfirmAction
          confirmLabel={
            intent.kind === "activate" ? "Activate approved profile" : "Cancel pending change"
          }
          danger={intent.kind === "cancel"}
          error={actionError}
          maximumReason={1000}
          minimumReason={20}
          onCancel={() => {
            setActionError(null);
            setIntent(null);
          }}
          onConfirm={(reason) => void confirm(reason)}
          pending={submitting}
          prompt={
            intent.kind === "activate"
              ? `Select ${intent.profile.config.configId} (${intent.profile.config.configSha256}). It will activate immediately only if the authoritative table is at a safe boundary; otherwise it remains pending.`
              : `Cancel ${pendingRequest?.target.configId ?? "the pending profile"} (${pendingRequest?.target.configSha256 ?? "identity unavailable"}). This does not select or activate a replacement.`
          }
          title={
            intent.kind === "activate"
              ? `Activate ${gameName} profile`
              : `Cancel pending ${gameName} profile`
          }
        />
      ) : null}
    </section>
  );
}
